// TEMPORARY — Zendesk cutover tool. Bulk import of organizations and end-users from Zendesk.
// Removed from the Admin landing page; route still wired in App.tsx for direct access.
// Delete this file, its route, and the backend endpoint together once cutover is done.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';

interface ImportResult {
  orgsScanned: number;
  orgsImported: number;
  orgsSkipped: number;
  orgFailures: Array<{ name: string; error: string }>;
  contactsScanned: number;
  contactsImported: number;
  contactsSkipped: number;
  contactFailures: Array<{ email: string; error: string }>;
}

interface Status {
  configured: boolean;
  subdomain: string | null;
}

export function AdminZendeskImportPage(): React.ReactElement {
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    apiFetch<Status>('/api/admin/zendesk-import/status')
      .then(setStatus)
      .catch(() => setStatus({ configured: false, subdomain: null }));
  }, []);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch<ImportResult>('/api/admin/zendesk-import/orgs-and-contacts', {
        method: 'POST',
        json: {},
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">
          Import organizations + contacts from Zendesk
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Pulls every organization and end-user (client) from Zendesk and creates them in T3.
          Re-runs are safe — orgs whose name already exists in T3 are skipped, contacts whose email
          already exists are skipped. KB articles use the separate "Import from Zendesk" button on
          the KB page.
        </p>
      </div>

      {status && !status.configured && (
        <Card className="border-amber-300 bg-amber-50/40">
          <p className="text-sm text-amber-900">
            Zendesk is not configured. Set <code>ZENDESK_SUBDOMAIN</code>,{' '}
            <code>ZENDESK_EMAIL</code>, and <code>ZENDESK_API_TOKEN</code> in your environment,
            then restart the backend.
          </p>
        </Card>
      )}

      {status && status.configured && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            Connected to{' '}
            <code className="text-[var(--color-body)]">{status.subdomain}.zendesk.com</code>.
          </p>
          {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="mt-3">
            <Button onClick={() => void run()} disabled={busy}>
              {busy ? 'Importing…' : 'Import orgs + contacts'}
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="border-green-300 bg-green-50/40">
          <h2 className="text-base font-semibold text-green-900">Import complete</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 text-sm text-green-900">
            <div>
              <h3 className="font-semibold">Organizations</h3>
              <ul className="mt-1 space-y-1">
                <li>Scanned: {result.orgsScanned}</li>
                <li>Imported: <strong>{result.orgsImported}</strong></li>
                <li>Skipped (already exist): {result.orgsSkipped}</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold">Contacts</h3>
              <ul className="mt-1 space-y-1">
                <li>Scanned: {result.contactsScanned}</li>
                <li>Imported: <strong>{result.contactsImported}</strong></li>
                <li>Skipped (already exist or no org match): {result.contactsSkipped}</li>
              </ul>
            </div>
          </div>
          {(result.orgFailures.length > 0 || result.contactFailures.length > 0) && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-[var(--color-danger)]">
                {result.orgFailures.length + result.contactFailures.length} failures (click to expand)
              </summary>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {result.orgFailures.map((f, i) => (
                  <li key={`o${i}`}><strong>org {f.name}</strong>: {f.error}</li>
                ))}
                {result.contactFailures.map((f, i) => (
                  <li key={`c${i}`}><strong>contact {f.email}</strong>: {f.error}</li>
                ))}
              </ul>
            </details>
          )}
          <div className="mt-3 flex gap-2">
            <Button variant="outline" onClick={() => navigate('/organizations')}>
              View organizations
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
